import { Account, Ed25519PrivateKey } from "@aptos-labs/ts-sdk";

import type { BN } from "@utils/BN";
import { submitTransaction } from "@utils/xchainTransaction";

import { aptosClient } from "@src/api/aptos";
import { getEkidenSdkClient, getVaultContract } from "@src/api/sdk";

import type { InputTransactionData } from "@aptos-labs/wallet-adapter-core";
import type { SubAccount, Wallet } from "../account/types";

export interface DepositParams {
  wallet: Wallet;
  fundingAddress: string;
  tradingAddress?: string;
  tokenAddress: string;
  amount: BN;
  depositTo: "funding" | "trading";
}

export interface WithdrawParams {
  wallet: Wallet;
  fundingAddress: string;
  tokenAddress: string;
  amount: BN;
}

export interface TransferParams {
  wallet: Wallet;
  vaultFrom: string;
  vaultTo: string;
  vaultFromType: string;
  vaultToType: string;
  amount: BN;
}

export interface TransferRequestParams {
  wallet: Wallet;
  fromSubAddress: string;
  toSubAddress: string;
  vaultToType: string;
  amount: BN;
}

export interface WithdrawFromTradingParams {
  tradingAccount: SubAccount;
  fundingAddress: string;
  tokenAddress: string;
  amount: string;
}

export class VaultService {
  deposit = async (params: DepositParams): Promise<string> => {
    const { wallet, fundingAddress, tradingAddress, tokenAddress, amount, depositTo } = params;

    const depositPayload = this.buildDepositPayload(tokenAddress, amount, fundingAddress, tradingAddress, depositTo);

    const walletPromise = submitTransaction({
      wallet,
      data: depositPayload,
    });

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Wallet timeout after 30 seconds")), 30000),
    );

    const depositResponse = await Promise.race([walletPromise, timeoutPromise]);
    await aptosClient.waitForTransaction({ transactionHash: (depositResponse as any).hash });

    return (depositResponse as any).hash;
  };

  withdraw = async (params: WithdrawParams): Promise<string> => {
    const { wallet, fundingAddress, tokenAddress, amount } = params;

    const withdrawPayload = getEkidenSdkClient().vaultOnChain.withdrawFromFunding({
      subAddress: fundingAddress,
      assetMetadata: tokenAddress,
      amount: amount.toBigInt(),
    });

    const withdrawResponse = await submitTransaction({
      wallet,
      data: withdrawPayload as any,
    });

    await aptosClient.waitForTransaction({ transactionHash: withdrawResponse.hash });

    return withdrawResponse.hash;
  };

  transfer = async (params: TransferParams): Promise<string> => {
    const { wallet, vaultFrom, vaultTo, vaultFromType, vaultToType, amount } = params;

    const transferPayload = getEkidenSdkClient().vaultOnChain.transfer({
      vaultAddress: getVaultContract(),
      vaultFrom,
      vaultTo,
      amount: amount.toBigInt(),
      vaultFromType: vaultFromType as any,
      vaultToType: vaultToType as any,
    });

    const transferResponse = await submitTransaction({
      wallet,
      data: transferPayload as any,
    });

    await aptosClient.waitForTransaction({ transactionHash: transferResponse.hash });

    return transferResponse.hash;
  };

  transferRequest = async (params: TransferRequestParams): Promise<string> => {
    const { wallet, fromSubAddress, toSubAddress, vaultToType, amount } = params;

    const transferRequestPayload = getEkidenSdkClient().vaultOnChain.transferRequest({
      vaultAddress: getVaultContract(),
      fromSubAddress,
      toSubAddress,
      amount: amount.toBigInt(),
      vaultToType: vaultToType as any,
    });

    const response = await submitTransaction({
      wallet,
      data: transferRequestPayload as any,
    });

    await aptosClient.waitForTransaction({ transactionHash: response.hash });

    return response.hash;
  };

  withdrawFromTrading = async (params: WithdrawFromTradingParams) => {
    const { tradingAccount, fundingAddress, tokenAddress, amount } = params;

    const privateKeyEd25519 = new Ed25519PrivateKey(tradingAccount.privateKey);
    const account = Account.fromPrivateKey({ privateKey: privateKeyEd25519 });

    const withdrawParams = getEkidenSdkClient().vault.buildWithdrawFromTradingParams(account, {
      addr_to: fundingAddress,
      amount,
      asset_metadata: tokenAddress,
      nonce: Number(tradingAccount.nonce || "0"),
    });

    return getEkidenSdkClient().vault.withdrawFromTrading(withdrawParams);
  };

  private buildDepositPayload = (
    tokenAddress: string,
    amount: BN,
    fundingAddress: string,
    tradingAddress: string | undefined,
    depositTo: "funding" | "trading",
  ): InputTransactionData["data"] => {
    if (depositTo === "funding") {
      return getEkidenSdkClient().vaultOnChain.depositIntoFunding({
        subAddress: fundingAddress,
        assetMetadata: tokenAddress,
        amount: amount.toBigInt(),
      }) as InputTransactionData["data"];
    }

    return getEkidenSdkClient().vaultOnChain.depositIntoFundingWithTransferTo({
      vaultAddress: getVaultContract(),
      fundingSubAddress: fundingAddress,
      tradingSubAddress: tradingAddress!,
      assetMetadata: tokenAddress,
      amount: amount.toBigInt(),
      vaultToType: "Cross",
    }) as InputTransactionData["data"];
  };
}

export const vaultService = new VaultService();
